import type { B2BChannelType, B2BRegion, ProductSortKey, ProductUnitListQuery } from '@tcr-erp/shared';
import type { ListWorkerJobFailuresQuery } from './queue-ops.service';
import type { B2BGiftDebtQuery } from './b2b-gift-debt.service';
import type { B2BOrderCreditApprovalQuery } from './b2b-order-credit-approval.service';
export type { ProductUnitListQuery } from '@tcr-erp/shared';

// ADR-013: API JSON snake_case end-to-end. Pagination key chuẩn = page_size
// (giống OrderListQuery). FE state useTableState giữ camelCase (TS variable),
// transform tại site gọi hook list (vd: { page_size: pageSize }).
export interface ProductListQuery {
  q?: string;
  // EG-1731: filter multi-select — mỗi field nhận 1+ giá trị (OR trong cùng field).
  category_id?: string[];
  brand_id?: string[];
  state?: number[];
  sale_type?: string[];
  page?: number;
  page_size?: number;
  sort?: ProductSortKey;
  attrs?: Record<string, string[]>; // multi-value per attribute
  /** TCR-1525 — lọc danh sách combo thật (màn /products/combos-fix) qua service sản phẩm. */
  is_combo?: boolean;
}

export interface FilterableAttributeValue {
  label: string;
  value: string;
}

export interface FilterableAttribute {
  code: string;
  name: string;
  values: FilterableAttributeValue[];
}

/** EG-78: snake_case property per ADR-013 D4. group → BE resolve thành array enum. */
export interface OrderListQuery {
  /** TCR-2473: tenant Gift-category orders and linked-document presence (drafts included). */
  gift_only?: boolean;
  has_sales_document?: 'true' | 'false';
  has_e_invoice?: 'true' | 'false';
  page?: number;
  page_size?: number;
  channel_group?: string;
  /**
   * Lọc theo nguồn đơn `Order.channel` (enum `OrderChannel` của tcr-erp-shared, vd `POS`). Dùng
   * cho bộ lọc "Liên kết đơn → Ngoài sàn" của phiếu yêu cầu Omnichat (TCR-2257).
   */
  channel?: string;
  /**
   * Lọc theo nền tảng đơn `Order.platform` (enum `Platform`: `online` | `offline` | `admin`). BE
   * `GET /orders` hỗ trợ tham số này từ commit 5cf54633 (TCR-2257) — `offline` = đơn POS tạo trực
   * tiếp (ngoài sàn), KHÔNG lẫn đơn POS online/admin.
   */
  platform?: string;
  /** Lọc theo ĐÚNG trạng thái đơn (`OrderState`), không gom nhóm. Nhiều giá trị ngăn bởi dấu phẩy. */
  status?: string;
  /** Nhóm trạng thái suy ra cho các tab màn Orders. */
  status_group?: string;
  user_type?: 'personal' | 'organization';
  /** Legacy business-date filter; applies to `order_date`. */
  date_from?: string;
  date_to?: string;
  /** Orders list display-date filter; applies to `created_at`. */
  created_at_from?: string;
  created_at_to?: string;
  /** Ô tìm kiếm tổng ở FilterBar: match mã đơn HOẶC tên/SĐT khách. */
  search?: string;
  /** Lọc riêng theo cột "Mã đơn". */
  order_number?: string;
  /** Lọc riêng theo cột "Khách hàng" (tên hoặc SĐT). */
  customer?: string;
  /** Khoảng tổng tiền (VND). Bỏ trống một đầu = mở đầu đó. */
  total_from?: number;
  total_to?: number;
  /** Sắp xếp phía server, dạng `<field>:<asc|desc>`. */
  sort?: string;
  /** TCR-1092: nhân viên tạo đơn (`process_by`) — ObjectId string, dùng `staff_id`/`_id` nhân viên. */
  created_by?: string;
  /** TCR-1092: bên trả phí vận chuyển. */
  fee_payer?: 'seller' | 'buyer';
  /** TCR-1092: đơn có/không có ghi chú của khách hàng (`notes.user_note`). */
  has_customer_note?: 'true' | 'false';
  /**
   * TCR-1092 vòng 2: trạng thái thanh toán, danh sách `PaymentState` ngăn bởi dấu phẩy.
   * Một lựa chọn của người dùng có thể phủ nhiều enum ("Chưa thanh toán" = PENDING + UNPAID)
   * nên gửi danh sách chứ không gửi một giá trị — xem `buildOrderApiFilters`.
   */
  payment_state?: string;
  /** TCR-1967: đã/chưa phát hành hoá đơn GTGT. */
  invoice_state?: 'issued' | 'not_issued';
  /** TCR-2625: đơn có / không có SP thuộc danh mục Gift (kể cả trong combo thật). */
  gift_filter?: 'has' | 'none';
  /**
   * TCR-2130: kênh khách B2B của đơn (`Order.b2b_channel`) — MT/GT/SI, mọi kênh bán. Khi người
   * dùng chọn cả lựa chọn Kênh gộp (vd "B2B Sỉ POS") thì giá trị này thắng.
   */
  b2b_channel?: 'MT' | 'GT' | 'SI';
  /** TCR-2129: chỉ đơn có dòng chiết khấu khác chính sách. */
  has_discount_deviation?: 'true';
  /** TCR-1092: đơn có/không có khách trả trước (`shipping.prepaid_amount > 0`). */
  prepaid?: 'true' | 'false';
  /**
   * TCR-1668: lọc theo chi nhánh (`Order.store_id`) — ObjectId string.
   *
   * `storeId` là key cũ; `store_id` là alias mới của API. Giữ cả hai để không phá caller cũ.
   */
  storeId?: string;
  store_id?: string;
}

export interface InventoryListQuery {
  page?: number;
  page_size?: number;
  /** Lọc theo ID danh mục sản phẩm. */
  category_id?: string;
  /** Tương thích ngược với một số caller cũ; inventory API chuẩn dùng category_id. */
  category?: string;
  karat?: string;
  store_id?: string;
  warehouse_id?: string;
  tracking_mode?: 'quantity' | 'serial';
  search?: string;
  group_by?: 'variant_warehouse' | 'variant_store' | 'variant_total';
}

/** TCR-2492 — giá trị `amis_status` BE nhận ở `GET /users`. */
export type AmisStatusFilter = 'matched' | 'needs_review' | 'unmapped';

export interface StaffListQuery {
  page?: number;
  page_size?: number;
  /** Filter by single role_id — matches if role_ids array contains this id. */
  role_id?: string;
  /** Filter by role alias from /settings/roles, e.g. inventory. */
  role_alias?: string;
  status?: string;
  search?: string;
  store_id?: string;
  /** TCR-2492 — lọc theo trạng thái map nhân viên AMIS. */
  amis_status?: AmisStatusFilter;
}

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (q: ProductListQuery) => [...productKeys.lists(), q] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
  variants: (productId: string) => [...productKeys.all, 'variants', productId] as const,
  variantDetail: (productId: string, variantId: string) =>
    [...productKeys.all, 'variants', productId, variantId] as const,
};

/** TCR-2038 — trạng thái đồng bộ hàng hoá sang các tổ chức AMIS. */
export const productAmisSyncKeys = {
  all: ['product-amis-sync'] as const,
  badges: (productId: string) => [...productAmisSyncKeys.all, 'badges', productId] as const,
  summary: () => [...productAmisSyncKeys.all, 'summary'] as const,
};

/** EG service catalog — snake_case params per ADR-013, page_size chuẩn giống ProductListQuery. */
export interface ServiceListQuery {
  q?: string;
  category_id?: string;
  state?: number;
  page?: number;
  page_size?: number;
  sort?: string;
}

export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (q: ServiceListQuery) => [...serviceKeys.lists(), q] as const,
  details: () => [...serviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceKeys.details(), id] as const,
};

export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (q: OrderListQuery) => [...orderKeys.lists(), q] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderKeys.details(), id] as const,
};

export const inventoryKeys = {
  all: ['inventory'] as const,
  lists: () => [...inventoryKeys.all, 'list'] as const,
  list: (q: InventoryListQuery) => [...inventoryKeys.lists(), q] as const,
  tree: (q: InventoryListQuery) => [...inventoryKeys.all, 'tree', q] as const,
  scopeSummary: (q: Pick<InventoryListQuery, 'store_id' | 'warehouse_id'>) =>
    [...inventoryKeys.all, 'scope-summary', q] as const,
  history: (productId: string) => [...inventoryKeys.all, 'history', productId] as const,
  movements: (q: Record<string, unknown>) => [...inventoryKeys.all, 'movements', q] as const,
};

export const staffKeys = {
  all: ['staffs'] as const,
  lists: () => [...staffKeys.all, 'list'] as const,
  list: (q: StaffListQuery) => [...staffKeys.lists(), q] as const,
  detail: (id: string) => [...staffKeys.all, 'detail', id] as const,
};

export const reportKeys = {
  all: ['reports'] as const,
  revenue: (range: { from: string; to: string }) => [...reportKeys.all, 'revenue', range] as const,
  eod: (date: string, storeId?: string) =>
    [...reportKeys.all, 'eod', date, storeId ?? null] as const,
  eodHistory: (query: { page: number; pageSize: number; storeId?: string }) =>
    [...reportKeys.all, 'eod-history', query] as const,
  profit: (query: { startDate: string; endDate: string; storeId?: string; groupBy?: string }) =>
    [...reportKeys.all, 'profit', query] as const,
  products: (query: {
    startDate: string;
    endDate: string;
    storeId?: string;
    sortBy?: string;
    limit?: number;
  }) => [...reportKeys.all, 'products', query] as const,
  staff: (query: { startDate: string; endDate: string; storeId?: string; sortBy?: string }) =>
    [...reportKeys.all, 'staff', query] as const,
  customersRfm: (query: { startDate: string; endDate: string; storeId?: string }) =>
    [...reportKeys.all, 'customers-rfm', query] as const,
  inventoryValuation: (query: { as_of_date?: string; store_id?: string; category?: string }) =>
    [...reportKeys.all, 'inventory-valuation', query] as const,
  builderSavedList: () => [...reportKeys.all, 'builder', 'saved-list'] as const,
};

export const planKeys = {
  all: ['plan'] as const,
  current: () => [...planKeys.all, 'current'] as const,
  payments: () => [...planKeys.all, 'payments'] as const,
  list: () => [...planKeys.all, 'list'] as const,
  paymentMethods: () => [...planKeys.all, 'payment-methods'] as const,
  paymentStatus: (paymentId: string) => [...planKeys.all, 'payment-status', paymentId] as const,
  subscription: () => [...planKeys.all, 'subscription'] as const,
  changePlanPreview: (slug: string) => [...planKeys.all, 'change-plan-preview', slug] as const,
  billingPayments: (page: number, limit: number) =>
    [...planKeys.all, 'billing-payments', page, limit] as const,
};

export const storeKeys = {
  all: ['store'] as const,
  settings: () => [...storeKeys.all, 'settings'] as const,
  storeSettings: (id: string) => [...storeKeys.all, 'store', id, 'settings'] as const,
  theme: () => [...storeKeys.all, 'theme'] as const,
  stores: () => [...storeKeys.all, 'stores'] as const,
  store: (id: string) => [...storeKeys.all, 'store', id] as const,
};

export const hardwareKeys = {
  all: ['hardware'] as const,
  status: () => [...hardwareKeys.all, 'status'] as const,
  devices: () => [...hardwareKeys.all, 'devices'] as const,
};

export const onboardingKeys = {
  all: ['onboarding'] as const,
  progress: () => [...onboardingKeys.all, 'progress'] as const,
};

export const roleKeys = {
  all: ['roles'] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  detail: (id: string) => [...roleKeys.all, 'detail', id] as const,
  omnichatPermissions: () => [...roleKeys.all, 'omnichat-permissions'] as const,
};

export interface CustomerListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  loyaltyTierId?: string;
  tag?: string;
  groupId?: string;
  type?: 'personal' | 'organization';
  assignedStaffId?: string;
  /**
   * TCR-2507 — lọc khách CHƯA có NVKD phụ trách. BE nhận riêng query
   * `unassigned_staff=true`; KHÔNG được gửi kèm `assigned_staff_id` cùng lúc
   * (BE từ chối khi có cả 2).
   */
  unassignedStaff?: boolean;
  state?: 'active' | 'inactive';
  partnerType?: 'customer' | 'supplier';
  totalSpentMin?: number;
  totalSpentMax?: number;
  loyaltyPointsMin?: number;
  loyaltyPointsMax?: number;
  lastPurchaseFrom?: string;
  lastPurchaseTo?: string;
  /** TCR-1553 — trạng thái đồng bộ AMIS/Misa. */
  amisSyncStatus?: 'synced' | 'pending' | 'not_synced';
  /** TCR-2070 — filter Kênh B2B/Miền ở trang Khách hàng B2B. */
  b2bChannel?: B2BChannelType;
  region?: B2BRegion;
}

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (q: CustomerListQuery) => [...customerKeys.lists(), q] as const,
  groups: (target_type?: string) => target_type
    ? [...customerKeys.all, 'groups', target_type] as const
    : [...customerKeys.all, 'groups'] as const,
  groupsDetail: (target_type?: string) => target_type
    ? [...customerKeys.all, 'groups-detail', target_type] as const
    : [...customerKeys.all, 'groups-detail'] as const,
  detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
  purchases: (id: string) => [...customerKeys.all, 'purchases', id] as const,
  addresses: (id: string) => [...customerKeys.all, 'addresses', id] as const,
  banks: (id: string) => [...customerKeys.all, 'banks', id] as const,
  creditSummary: (id: string) => [...customerKeys.all, 'credit-summary', id] as const,
  creditTransactions: (id: string, page: number) =>
    [...customerKeys.all, 'credit-transactions', id, page] as const,
  /** TCR-1552 — lịch sử thay đổi (ai sửa gì, khi nào). */
  logs: (id: string, page: number) => [...customerKeys.all, 'logs', id, page] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: (scope?: 'mine') => [...notificationKeys.all, 'unreadCount', scope ?? 'all'] as const,
};

export const shiftKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftKeys.all, 'list'] as const,
  current: () => [...shiftKeys.all, 'current'] as const,
  detail: (id: string) => [...shiftKeys.all, 'detail', id] as const,
  daily: (date: string, storeId?: string) =>
    [...shiftKeys.all, 'daily', date, storeId ?? 'all'] as const,
};

export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: (role?: string, params?: Record<string, unknown>) =>
    [...dashboardKeys.all, 'summary', role ?? 'any', params ?? {}] as const,
  chartSales: (params?: Record<string, unknown>) =>
    [...dashboardKeys.all, 'chart_sales', params ?? {}] as const,
  topProducts: (params?: Record<string, unknown>) =>
    [...dashboardKeys.all, 'top_products', params ?? {}] as const,
};

export interface ShippingListQuery {
  page?: number;
  limit?: number;
  state?: string;
  /** Kèm nhóm huỷ khi app shipper lọc tab RETURN gộp. */
  include_cancelled?: boolean;
  provider?: string;
  /** Tiến trình gửi hãng — tách khỏi `state` (vòng đời giao hàng). */
  submission_state?: string;
  q?: string;
  /** Tìm trong địa chỉ người nhận: chuỗi tự do, Phường/Xã, hoặc Tỉnh/Thành. */
  address?: string;
  store_id?: string;
  /**
   * Chỉ lấy vận đơn gán cho CHÍNH người đang đăng nhập (TCR-1521).
   *
   * Máy chủ lấy id từ token chứ không tin tham số này, và nó GHI ĐÈ
   * `deliveryman_staff_id` nếu gửi cả hai — xem `shipping.service.ts` bên backend.
   */
  assigned_to_me?: boolean;
  /** Chỉ lấy vận đơn tự giao chưa phân công mà shipper hiện tại có thể nhận. */
  claimable_by_me?: boolean;
  /** Lọc theo một người giao cụ thể — dành cho điều độ, không phải app shipper. */
  deliveryman_staff_id?: string;
  /** Tháng lọc, định dạng YYYY-MM; tab Đã giao được BE áp dụng lên delivered_at. */
  month?: string;
}

export type ShipperShippingListQuery = Omit<ShippingListQuery, 'page' | 'limit'> & {
  state: string;
};

export interface GhtkLogsQuery {
  page?: number;
  limit?: number;
  order_number?: string;
}

/** EG-332/345 — Settings (Owner-only tenant config) */
export const settingsKeys = {
  all: ['settings'] as const,
  full: () => [...settingsKeys.all, 'full'] as const,
  locale: () => [...settingsKeys.all, 'locale'] as const,
  general: () => [...settingsKeys.all, 'general'] as const,
  business: () => [...settingsKeys.all, 'business'] as const,
  payment: () => [...settingsKeys.all, 'payment'] as const,
  einvoice: () => [...settingsKeys.all, 'einvoice'] as const,
  notifications: () => [...settingsKeys.all, 'notifications'] as const,
  integrations: () => [...settingsKeys.all, 'integrations'] as const,
  bankAccounts: () => [...settingsKeys.all, 'bank-accounts'] as const,
  eWallets: () => [...settingsKeys.all, 'e-wallets'] as const,
  cardTerminals: () => [...settingsKeys.all, 'card-terminals'] as const,
  apiKeys: () => [...settingsKeys.all, 'api-keys'] as const,
  auditLog: (q?: Record<string, unknown>) => [...settingsKeys.all, 'audit-log', q ?? {}] as const,
  /** Danh sách mọi parent store + trạng thái kết nối AMIS (multi-store). */
  amisConnections: () => [...settingsKeys.all, 'accounting-sync', 'amis', 'connections'] as const,
  amisStatus: (parentStoreId: string | undefined) =>
    [...settingsKeys.all, 'accounting-sync', 'amis', 'status', parentStoreId ?? ''] as const,
  amisBranches: (parentStoreId: string | undefined) =>
    [...settingsKeys.all, 'accounting-sync', 'amis', 'branches', parentStoreId ?? ''] as const,
  integrationSettings: (provider: string, section: string) =>
    [...settingsKeys.all, 'integrations', provider, section] as const,
  amisMasterdataHistory: (params?: Record<string, unknown>) =>
    [...settingsKeys.all, 'accounting-sync', 'amis', 'history', params ?? {}] as const,
  /** Prefix của mọi trang/bộ lọc trong 1 catalog — dùng để invalidate mà không cần biết params. */
  amisMasterdataListAll: (catalog: string) =>
    [...settingsKeys.all, 'accounting-sync', 'amis', 'masterdata', catalog] as const,
  amisMasterdataList: (catalog: string, params?: Record<string, unknown>) =>
    [...settingsKeys.amisMasterdataListAll(catalog), params ?? {}] as const,
};

/**
 * TCR-2523 pha 2 — "Tồn kho AMIS → số dư đầu kỳ".
 *
 * Key nằm dưới `accounting` để invalidate được theo nhóm kế toán.
 */
export const amisInventoryBalanceKeys = {
  all: ['accounting', 'amis', 'inventory-balance'] as const,
  snapshots: (params?: Record<string, unknown>) =>
    [...amisInventoryBalanceKeys.all, 'snapshots', params ?? {}] as const,
  snapshot: (id: string) => [...amisInventoryBalanceKeys.all, 'snapshot', id] as const,
  rows: (id: string, params?: Record<string, unknown>) =>
    [...amisInventoryBalanceKeys.all, 'rows', id, params ?? {}] as const,
  diff: (id: string, params?: Record<string, unknown>) =>
    [...amisInventoryBalanceKeys.all, 'diff', id, params ?? {}] as const,
  /**
   * Bảng "tồn theo kho" của 1 lần kéo.
   *
   * Key PHẢI gồm cả `snapshotId` lẫn `store_id` — cùng 1 lần kéo nhưng khác
   * chi nhánh ERP cho ra số tồn ERP khác nhau.
   */
  warehouses: (id: string, params?: Record<string, unknown>) =>
    [...amisInventoryBalanceKeys.all, 'warehouses', id, params ?? {}] as const,
};

/** EG-504..EG-529 — Payroll module */
export const payrollKeys = {
  all: ['payroll'] as const,
  config: () => [...payrollKeys.all, 'config'] as const,
  components: () => [...payrollKeys.all, 'components'] as const,
  profile: (userId: string) => [...payrollKeys.all, 'profile', userId] as const,
  runs: () => [...payrollKeys.all, 'runs'] as const,
  runDetail: (id: string) => [...payrollKeys.runs(), 'detail', id] as const,
  dashboard: () => [...payrollKeys.all, 'dashboard'] as const,
  reports: () => [...payrollKeys.all, 'reports'] as const,
};

export const shippingKeys = {
  all: ['shipping'] as const,
  lists: () => [...shippingKeys.all, 'list'] as const,
  list: (q: ShippingListQuery) => [...shippingKeys.lists(), q] as const,
  // Nằm dưới `lists()` để mọi mutation giao vận hiện có invalidate list chung
  // vẫn làm mới được app shipper ngay, không phải chờ lượt polling kế tiếp.
  shipperLists: () => [...shippingKeys.lists(), 'shipper'] as const,
  shipperGroup: (stateGroup: string) => [...shippingKeys.shipperLists(), stateGroup] as const,
  shipperList: (q: ShipperShippingListQuery) => [
    ...shippingKeys.shipperGroup(q.state),
    q.month ?? 'all',
    q,
  ] as const,
  claimableForShipper: (month: string) => [
    ...shippingKeys.shipperLists(),
    'claimable',
    month,
  ] as const,
  myStateCounts: (month?: string) => [
    ...shippingKeys.lists(),
    'my-state-counts',
    month ?? 'all',
  ] as const,
  myProfileStats: () => [...shippingKeys.lists(), 'my-profile-stats'] as const,
  myDeliveryStats: (year: number) => [...shippingKeys.lists(), 'my-delivery-stats', year] as const,
  detail: (id: string) => [...shippingKeys.all, 'detail', id] as const,
  tracking: (id: string) => [...shippingKeys.all, 'tracking', id] as const,
  pickupLocations: () => [...shippingKeys.all, 'pickup-locations'] as const,
  ghtkLogs: (q: GhtkLogsQuery) => [...shippingKeys.all, 'ghtk-logs', q] as const,
};

// === Products module — master data (Phase 1) ===

export interface CategoryListQuery {
  parent_id?: string;
}

export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (q: CategoryListQuery) => [...categoryKeys.lists(), q] as const,
  tree: () => [...categoryKeys.all, 'tree'] as const,
  detail: (id: string) => [...categoryKeys.all, 'detail', id] as const,
};

export interface BrandListQuery {
  q?: string;
  page?: number;
  page_size?: number;
}

export const brandKeys = {
  all: ['brands'] as const,
  lists: () => [...brandKeys.all, 'list'] as const,
  list: (q: BrandListQuery) => [...brandKeys.lists(), q] as const,
  detail: (id: string) => [...brandKeys.all, 'detail', id] as const,
};

export interface B2BPriceListQuery {
  brand_id?: string;
  status?: string;
  search?: string;
  effective_from_start?: string;
  effective_from_end?: string;
  /** TCR-1939 — lọc theo kênh (MT/GT/SI), xem `B2BChannelType` ở `@tcr-erp/shared`. */
  channel?: string;
  page?: number;
  page_size?: number;
}

export const b2bPriceListKeys = {
  all: ['b2b/price-lists'] as const,
  lists: () => [...b2bPriceListKeys.all, 'list'] as const,
  list: (q: B2BPriceListQuery) => [...b2bPriceListKeys.lists(), q] as const,
  detail: (id: string) => [...b2bPriceListKeys.all, 'detail', id] as const,
  compare: (a?: string, b?: string) => [...b2bPriceListKeys.all, 'compare', a || '', b || ''] as const,
  history: (brandId?: string, page?: number) => [...b2bPriceListKeys.all, 'history', brandId || '', page || 1] as const,
  snapshot: (brandId?: string, atDate?: string) => [...b2bPriceListKeys.all, 'snapshot', brandId || '', atDate || ''] as const,
  myScope: () => [...b2bPriceListKeys.all, 'my-scope'] as const,
};

export interface B2BPriceOverrideQuery {
  customer_id?: string;
  brand_id?: string;
  status?: 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED' | 'EXPIRED' | 'DELETED';
  search?: string;
  page?: number;
  page_size?: number;
}

export const b2bPriceOverrideKeys = {
  all: ['b2b/price-overrides'] as const,
  lists: () => [...b2bPriceOverrideKeys.all, 'list'] as const,
  list: (q: B2BPriceOverrideQuery) => [...b2bPriceOverrideKeys.lists(), q] as const,
  detail: (id: string) => [...b2bPriceOverrideKeys.all, 'detail', id] as const,
  audit: (id: string) => [...b2bPriceOverrideKeys.all, 'audit', id] as const,
  history: (brandId?: string, page?: number) =>
    [...b2bPriceOverrideKeys.all, 'history', brandId || '', page || 1] as const,
};

export interface B2BReceivableQuery {
  q?: string;
  status?: 'OPEN' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'VOID';
  customer_id?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}

/** Filter NVKD/Brand dùng chung cho dashboard/aging/overdue-alerts/credit-limits (TCR-461). */
export interface B2BReceivableScopeQuery {
  salesperson_id?: string;
  brand_id?: string;
}

export interface B2BOverdueAlertsQuery extends B2BReceivableScopeQuery {
  overdue_days?: number;
}

export interface B2BCreditLimitsQuery extends B2BReceivableScopeQuery {
  q?: string;
  page?: number;
  page_size?: number;
}

export const b2bReceivableKeys = {
  debtReminders: (customerId: string) => ['b2b-receivables', 'debt-reminders', customerId] as const,
  all: ['b2b-receivables'] as const,
  lists: () => [...b2bReceivableKeys.all, 'list'] as const,
  list: (q: B2BReceivableQuery) => [...b2bReceivableKeys.lists(), q] as const,
  overview: (filters: B2BReceivableScopeQuery = {}) => [...b2bReceivableKeys.all, 'overview', filters] as const,
  dashboard: (filters: B2BReceivableScopeQuery = {}) => [...b2bReceivableKeys.all, 'dashboard', filters] as const,
  aging: (filters: B2BReceivableScopeQuery = {}) => [...b2bReceivableKeys.all, 'aging', filters] as const,
  customerOverdue: (customerId: string) =>
    [...b2bReceivableKeys.all, 'customer-overdue', customerId] as const,
  overdueAlerts: (filters: B2BOverdueAlertsQuery = {}) =>
    [...b2bReceivableKeys.all, 'overdue-alerts', filters] as const,
  staffOptions: () => [...b2bReceivableKeys.all, 'staff-options'] as const,
  creditLimit: (customerId: string) =>
    [...b2bReceivableKeys.all, 'credit-limit', customerId] as const,
  creditLimits: (query: B2BCreditLimitsQuery = {}) =>
    [...b2bReceivableKeys.all, 'credit-limits', query] as const,
  overLimitCustomerIds: () =>
    [...b2bReceivableKeys.all, 'over-limit-customer-ids'] as const,
  customerReceivables: (customerId: string) =>
    [...b2bReceivableKeys.all, 'customer-receivables', customerId] as const,
};

export const b2bGiftDebtKeys = {
  all: ['b2b-gift-debts'] as const,
  lists: () => [...b2bGiftDebtKeys.all, 'list'] as const,
  list: (q: B2BGiftDebtQuery) => [...b2bGiftDebtKeys.lists(), q] as const,
};

/** TCR-1522 Task 8 — duyệt đơn B2B vượt hạn mức công nợ (`ke_toan_truong` only). */
export const b2bOrderCreditApprovalKeys = {
  all: ['b2b-order-credit-approvals'] as const,
  lists: () => [...b2bOrderCreditApprovalKeys.all, 'list'] as const,
  list: (q: B2BOrderCreditApprovalQuery) => [...b2bOrderCreditApprovalKeys.lists(), q] as const,
};

export interface B2BRebatePolicyQuery {
  brand_id?: string;
  period_type?: 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'QUARTERLY_AND_YEARLY';
  status?: 'DRAFT' | 'PENDING' | 'REJECTED' | 'ACTIVE' | 'EXPIRED';
  search?: string;
  page?: number;
  page_size?: number;
}

export const b2bRebatePolicyKeys = {
  all: ['b2b/rebate-policies'] as const,
  lists: () => [...b2bRebatePolicyKeys.all, 'list'] as const,
  list: (q: B2BRebatePolicyQuery) => [...b2bRebatePolicyKeys.lists(), q] as const,
  detail: (id: string) => [...b2bRebatePolicyKeys.all, 'detail', id] as const,
  accruals: () => [...b2bRebatePolicyKeys.all, 'accruals'] as const,
  accrualDashboard: (q: object) => [...b2bRebatePolicyKeys.accruals(), 'dashboard', q] as const,
  accrualDashboardByCustomer: (q: object) =>
    [...b2bRebatePolicyKeys.accruals(), 'dashboard-by-customer', q] as const,
};

export const b2bRebateSettlementKeys = {
  all: ['b2b/rebate-settlements'] as const,
  list: (query: object) => [...b2bRebateSettlementKeys.all, 'list', query] as const,
  history: (query: object) => [...b2bRebateSettlementKeys.all, 'history', query] as const,
  detail: (id: string) => [...b2bRebateSettlementKeys.all, 'detail', id] as const,
};

export const b2bRebateJobKeys = {
  all: ['b2b/rebate-jobs'] as const,
  list: (query: Record<string, unknown>) =>
    [...b2bRebateJobKeys.all, 'list', query] as const,
  attention: () => [...b2bRebateJobKeys.all, 'attention'] as const,
  detail: (id: string) => [...b2bRebateJobKeys.all, 'detail', id] as const,
};

export const queueOpsKeys = {
  all: ['ops/queues'] as const,
  summary: () => [...queueOpsKeys.all, 'summary'] as const,
  schedules: () => [...queueOpsKeys.all, 'schedules'] as const,
  failures: (query: ListWorkerJobFailuresQuery) =>
    [...queueOpsKeys.all, 'failures', query] as const,
};

export const b2bRebateLedgerKeys = {
  all: ['b2b/rebate-ledger'] as const,
  list: (query: object) => [...b2bRebateLedgerKeys.all, 'list', query] as const,
  detail: (customerId: string, query: object) =>
    [...b2bRebateLedgerKeys.all, 'detail', customerId, query] as const,
};

export const productUnitKeys = {
  all: ['product-units'] as const,
  lists: () => [...productUnitKeys.all, 'list'] as const,
  list: (q: ProductUnitListQuery) => [...productUnitKeys.lists(), q] as const,
  detail: (id: string) => [...productUnitKeys.all, 'detail', id] as const,
};

export interface AttributeListQuery {
  q?: string;
  page?: number;
  limit?: number;
  sort?: 'code' | 'name' | 'created_at';
  order?: 'asc' | 'desc';
}

export const attributeKeys = {
  all: ['attributes'] as const,
  lists: () => [...attributeKeys.all, 'list'] as const,
  list: (q: AttributeListQuery) => [...attributeKeys.lists(), q] as const,
  detail: (code: string) => [...attributeKeys.all, 'detail', code] as const,
};

export const attributeValueKeys = {
  all: ['attribute-values'] as const,
  byAttribute: (code: string) => [...attributeValueKeys.all, 'by-attribute', code] as const,
  detail: (code: string, valueId: string) =>
    [...attributeValueKeys.all, 'detail', code, valueId] as const,
};

// === Accounting module (S4.1) ===

export interface EInvoiceListQuery {
  page?: number;
  limit?: number;
  store_id?: string;
  status?: string;
  invoice_kind?: string;
  reference_type?: string;
  sales_channel?: string;
  provider?: string;
  direction?: 'output' | 'input';
  from_date?: string;
  to_date?: string;
  search?: string;
}

export interface GovInvoiceListQuery {
  page?: number;
  limit?: number;
  status?: string;
  from_date?: string;
  to_date?: string;
}

export interface SalesReturnListQuery {
  page?: number;
  limit?: number;
  from_date?: string;
  to_date?: string;
  status?: string;
}

export interface PurchaseInvoiceListQuery {
  page?: number;
  limit?: number;
  from_date?: string;
  to_date?: string;
  status?: string;
  supplier_id?: string;
}

export interface PrepaidExpenseListQuery {
  page?: number;
  limit?: number;
  status?: string;
}

export const accountingKeys = {
  all: ['accounting'] as const,

  // Reports
  apSummary: (q: { from: string; to: string; account_code: string; store_id?: string }) =>
    [...accountingKeys.all, 'reports', 'ap-summary', q] as const,
  arSummary: (q: { from: string; to: string; account_code: string }) =>
    [...accountingKeys.all, 'reports', 'ar-summary', q] as const,

  // E-Invoices
  eInvoices: () => [...accountingKeys.all, 'e-invoices'] as const,
  eInvoiceList: (q?: EInvoiceListQuery) => [...accountingKeys.eInvoices(), 'list', q] as const,
  eInvoiceDetail: (id: string) => [...accountingKeys.eInvoices(), 'detail', id] as const,
  eInvoiceStats: () => [...accountingKeys.eInvoices(), 'stats'] as const,

  // Gov Invoices (TCT)
  govInvoices: () => [...accountingKeys.all, 'gov-invoices'] as const,
  govInvoiceList: (q?: GovInvoiceListQuery) =>
    [...accountingKeys.govInvoices(), 'list', q] as const,

  // Sales Returns
  salesReturns: () => [...accountingKeys.all, 'sales-returns'] as const,
  salesReturnList: (q?: SalesReturnListQuery) =>
    [...accountingKeys.salesReturns(), 'list', q] as const,

  // Purchase Invoices
  purchaseInvoices: () => [...accountingKeys.all, 'purchase-invoices'] as const,
  purchaseInvoiceList: (q?: PurchaseInvoiceListQuery) =>
    [...accountingKeys.purchaseInvoices(), 'list', q] as const,
  purchaseInvoiceDetail: (id: string) =>
    [...accountingKeys.purchaseInvoices(), 'detail', id] as const,

  // Prepaid Expenses
  prepaidExpenses: () => [...accountingKeys.all, 'prepaid-expenses'] as const,
  prepaidExpenseList: (q?: PrepaidExpenseListQuery) =>
    [...accountingKeys.prepaidExpenses(), 'list', q] as const,
  prepaidExpenseDetail: (id: string) =>
    [...accountingKeys.prepaidExpenses(), 'detail', id] as const,
  prepaidExpenseSummary: (id: string) =>
    [...accountingKeys.prepaidExpenses(), 'summary', id] as const,
  monthlyAllocations: (year?: number, month?: number) =>
    [...accountingKeys.prepaidExpenses(), 'monthly', year, month] as const,
};

export const warehouseKeys = {
  all: ['warehouses'] as const,
  lists: () => [...warehouseKeys.all, 'list'] as const,
  detail: (id: string) => [...warehouseKeys.all, 'detail', id] as const,
};

// === Fixed Assets (TSCĐ) ===

export interface FixedAssetGroupListQuery {
  page?: number;
  limit?: number;
  search?: string;
}

export interface FixedAssetListQuery {
  page?: number;
  limit?: number;
  status?: string;
  group_id?: string;
  department_id?: string;
  search?: string;
}

export interface FixedAssetDepreciationListQuery {
  fixed_asset_id?: string;
  year?: number;
  month?: number;
  status?: string;
}

export const fixedAssetKeys = {
  all: ['fixed-assets'] as const,
  groups: () => [...fixedAssetKeys.all, 'groups'] as const,
  groupList: (q?: FixedAssetGroupListQuery) => [...fixedAssetKeys.groups(), 'list', q] as const,
  groupDetail: (id: string) => [...fixedAssetKeys.groups(), 'detail', id] as const,
  assets: () => [...fixedAssetKeys.all, 'assets'] as const,
  assetList: (q?: FixedAssetListQuery) => [...fixedAssetKeys.assets(), 'list', q] as const,
  assetDetail: (id: string) => [...fixedAssetKeys.assets(), 'detail', id] as const,
  depreciation: (assetId: string) => [...fixedAssetKeys.all, 'depreciation', assetId] as const,
  monthlyDepreciation: (year?: number, month?: number) =>
    [...fixedAssetKeys.all, 'monthly-depreciation', year, month] as const,
  pendingDepreciation: () => [...fixedAssetKeys.all, 'pending-depreciation'] as const,
};

// === Tool & Supplies (CCDC) ===

export interface ToolSupplyListQuery {
  page?: number;
  limit?: number;
  status?: string;
  department_id?: string;
  warehouse_id?: string;
  search?: string;
}

export const toolSupplyKeys = {
  all: ['tool-supplies'] as const,
  list: (q?: ToolSupplyListQuery) => [...toolSupplyKeys.all, 'list', q] as const,
  detail: (id: string) => [...toolSupplyKeys.all, 'detail', id] as const,
  summary: (id: string) => [...toolSupplyKeys.all, 'summary', id] as const,
  monthlyAllocations: (year?: number, month?: number) =>
    [...toolSupplyKeys.all, 'monthly-allocations', year, month] as const,
  accountingTrail: (id: string) => [...toolSupplyKeys.all, 'accounting-trail', id] as const,
};

// === Opening Balances (Số dư đầu kỳ) ===

export const openingBalanceKeys = {
  all: ['opening-balances'] as const,
  tree: () => [...openingBalanceKeys.all, 'tree'] as const,
  summary: () => [...openingBalanceKeys.all, 'summary'] as const,
  details: (type: string, storeId: string, page = 1, limit = 20, search = '', extra?: string) =>
    [...openingBalanceKeys.all, 'details', type, storeId, page, limit, search, extra ?? ''] as const,
  history: (accountId: string) => [...openingBalanceKeys.all, 'history', accountId] as const,
};

// === B2B Staff Inventory (Tồn kho theo NVKD) ===

export interface B2BStaffInventoryQuery {
  warehouse_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export const b2bStaffInventoryKeys = {
  all: ['b2b-staff-inventory'] as const,
  lists: () => [...b2bStaffInventoryKeys.all, 'list'] as const,
  list: (q: B2BStaffInventoryQuery) => [...b2bStaffInventoryKeys.lists(), q] as const,
  detail: (staffId: string) => [...b2bStaffInventoryKeys.all, 'detail', staffId] as const,
};

// === Omnichat (P1) ===

export const omnichatKeys = {
  all: ['omnichat'] as const,
  connections: () => [...omnichatKeys.all, 'connections'] as const,
  metaApp: () => [...omnichatKeys.all, 'meta-app'] as const,
  publicUrls: () => [...omnichatKeys.all, 'public-urls'] as const,
  conversations: (q?: Record<string, unknown>) => [...omnichatKeys.all, 'conversations', q ?? {}] as const,
  tags: (activeOnly = true) => [...omnichatKeys.all, 'tags', activeOnly] as const,
  conversation: (id: string | undefined) => [...omnichatKeys.all, 'conversation', id ?? ''] as const,
  messages: (id: string | undefined) => [...omnichatKeys.all, 'messages', id ?? ''] as const,
  connectSession: (nonce: string | undefined) => [...omnichatKeys.all, 'connect-session', nonce ?? ''] as const,
  orders: (id: string | undefined) => [...omnichatKeys.all, 'conversation-orders', id ?? ''] as const,
  tiktokAvailableShops: () => [...omnichatKeys.all, 'tiktok-available-shops'] as const,
  shopeeAvailableShops: () => [...omnichatKeys.all, 'shopee-available-shops'] as const,
  ticketCategories: (activeOnly = true) => [...omnichatKeys.all, 'ticket-categories', activeOnly] as const,
  tickets: (conversationId: string | undefined) => [...omnichatKeys.all, 'tickets', conversationId ?? ''] as const,
  workTickets: (status?: string, assigneeId?: string) => [...omnichatKeys.all, 'work-tickets', status ?? 'all', assigneeId ?? 'all'] as const,
  cannedResponses: (categoryIds: string[] = [], statuses: string[] = ['approved']) =>
    [...omnichatKeys.all, 'canned-responses', categoryIds, statuses] as const,
  careRules: () => [...omnichatKeys.all, 'care-rules'] as const,
  careTasks: (query?: Record<string, unknown>) => [...omnichatKeys.all, 'care-tasks', query ?? {}] as const,
  campaigns: (query?: Record<string, unknown>) => [...omnichatKeys.all, 'campaigns', query ?? {}] as const,
  campaignResults: (id: string | undefined) => [...omnichatKeys.all, 'campaign-results', id ?? ''] as const,
  slaPolicies: () => [...omnichatKeys.all, 'sla-policies'] as const,
  slaOverview: () => [...omnichatKeys.all, 'sla-overview'] as const,
  slaSummary: (query: Record<string, unknown>) => [...omnichatKeys.all, 'sla-summary', query] as const,
};
